<template>
  <BgImageLayout>
    <Grid gtr="auto auto 1fr" gap="var(--r-space-md)" class="subscription-show full-height">
      <InfoCard
        :title="title || 'Loading...'"
        :popup-items="popupItems"
        @delete-subscription="deleteSubscription"
        @toggle-one-time-purchase="toggleToOneTimePurchase"
        @edit-current-period="onEditPeriod('current')"
        @edit-next-period="onEditPeriod('next')"
      >
        <ConfirmationDialog
          ref="confirmDialog"
          cancel-label="keep resource"
          confirm-label="delete subscription"
          confirm-btn-type="danger"
        />

        <EditSubscriptionModal
          v-if="editModal.model"
          v-model="editModal.model"
          :edit-item="editModal.period"
          :product="subscription.product"
        />

        <Grid
          class="boxes-wrapper"
          gtc="1fr calc(var(--r-space-lg) * 2 + 1px) 1fr calc(var(--r-space-lg) * 2 + 1px) 1fr"
        >
          <FirstBox />

          <LineSeparator class="q-px-lg" />

          <SecondBox />

          <LineSeparator class="q-px-lg" />

          <ThirdBox />
        </Grid>
      </InfoCard>

      <RouteTabs :tabs="tabs">
        <template v-slot="{ tab }">
          {{ tab.name }}

          <template v-if="tab.name === 'Notes & Tasks' && showBadge">
            ({{ subscription.incomplete_to_dos_count }})
          </template>
        </template>
      </RouteTabs>

      <keep-alive exclude="WorkflowTab">
        <router-view />
      </keep-alive>
    </Grid>
  </BgImageLayout>
</template>

<script>
import SubscriptionShowModule from './vuex/SubscriptionShow'
import * as apiTo from '@/modular-api'
import { rerenderOnIdChange } from '@/mixins/rerender-on-id-change'
// Libraries
import { mapGetters, mapMutations, mapActions } from 'vuex'
// Components
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import RouteTabs from '@/ui/RouteTabs'
import FirstBox from './components/TopBar/FirstBox.vue'
import SecondBox from './components/TopBar/SecondBox/SecondBox.vue'
import ThirdBox from './components/TopBar/ThirdBox/ThirdBox.vue'
import InfoCard from '@/ui/InfoCard'
import LineSeparator from './components/LineSeparator'
import EditSubscriptionModal from './components/EditSubscriptionModal/EditSubscriptionModal.vue'

export default {
  components: {
    ConfirmationDialog,
    RouteTabs,
    FirstBox,
    SecondBox,
    ThirdBox,
    InfoCard,
    LineSeparator,
    EditSubscriptionModal
  },

  mixins: [rerenderOnIdChange()],

  props: {
    id: [Number, String]
  },

  data() {
    return {
      editModal: {
        model: false,
        period: null
      },
      tabs: [
        {
          name: 'Workflow',
          route: `/subscriptions/${this.id}/workflow`
        },
        { name: 'Price', route: `/subscriptions/${this.id}/price` },
        { name: 'Usage', route: `/subscriptions/${this.id}/usage` },
        { name: 'Content', route: `/subscriptions/${this.id}/content` },
        { name: 'License', route: `/subscriptions/${this.id}/license` },
        {
          name: 'Notes & Tasks',
          route: `/subscriptions/${this.id}/notes-tasks`
        },
        {
          name: 'Evaluations',
          route: `/subscriptions/${this.id}/evaluations`
        },
        {
          name: 'Orders & Invoices',
          route: `/subscriptions/${this.id}/orders-invoices`
        },
        {
          name: 'Title List',
          route: `/subscriptions/${this.id}/title-list`
        },
        {
          name: 'Implementations',
          route: `/subscriptions/${this.id}/implementations`
        },
        {
          name: 'Emails & History',
          route: `/subscriptions/${this.id}/emails-history`
        }
      ]
    }
  },

  computed: {
    ...mapGetters('subscription-show', ['subscription']),

    popupItems() {
      if (!this.subscription) return

      const { one_time_purchase, subscription_periods_count, current, next } = this.subscription
      const disableOneTimePurchase = !one_time_purchase && subscription_periods_count > 1

      return [
        { name: 'edit-current-period', label: 'Edit current period', disable: !current },
        { name: 'edit-next-period', label: 'Edit next period', disable: !next || next.status.name === 'cancelled' },
        {
          name: 'toggle-one-time-purchase',
          label: one_time_purchase ? 'Change to a period subscription' : 'Change to one time purchase',
          disable: disableOneTimePurchase,
          borderTop: true,
          borderBottom: true,
          hover: disableOneTimePurchase
            ? 'This subscription cannot be changed to a one time \n purchase because it has more than one subscription period'
            : null
        },
        { name: 'delete-subscription', label: 'Delete subscription', textColorClass: 'text-danger-1' }
      ]
    },

    showBadge() {
      return this.subscription && this.subscription.incomplete_to_dos_count
    },

    title() {
      if (!this.subscription) return
      return this.subscription.product.name
    }
  },

  beforeCreate() {
    this.isRouterLeaving = false
    if (this.$store.hasModule('subscription-show')) {
      this.$store.unregisterModule('subscription-show')
    }
    this.$store.registerModule('subscription-show', SubscriptionShowModule)
  },

  created() {
    apiTo.GET_SUBSCRIPTION(this.id).then(({ data }) => this.setSubscription(data.result))
    this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
    this.$store.dispatch('licensePeriods/getCreateFormData')
    this.getAndSetSubscriptionPeriods(this.id)
  },

  beforeDestroy() {
    if (this.isRouterLeaving) this.$store.unregisterModule('subscription-show')
  },

  beforeRouteLeave(to, from, next) {
    this.isRouterLeaving = true
    next()
  },

  methods: {
    ...mapMutations('subscription-show', ['setSubscription', 'updateSubscriptionOneTimePurchase']),
    ...mapActions('subscription-show', ['getAndSetSubscriptionPeriods']),

    onEditPeriod(currentOrNext) {
      this.editModal = {
        model: true,
        period: this.subscription[currentOrNext]
      }
    },

    async toggleToOneTimePurchase() {
      await apiTo.MODIFY_SUBSCRIPTION(this.subscription.id, { one_time_purchase: !this.subscription.one_time_purchase })
      apiTo.GET_SUBSCRIPTION(this.subscription.id).then(({ data }) => {
        this.setSubscription(data.result)
        this.updateSubscriptionOneTimePurchase(data.result)
      })
    },

    deleteSubscription() {
      this.$refs.confirmDialog
        .show({
          title: 'Are you sure you want to delete this subscription?\n' + 'We will also delete subscription periods!'
        })
        .onConfirm((hide, setLoading) => {
          setLoading()
          apiTo
            .DELETE_SUBSCRIPTION(this.subscription.id)
            .then(this.redirectToIndex)
            .catch(() => hide())
        })
    },

    redirectToIndex() {
      this.$router.push({ path: '/subscriptions' })
    }
  }
}
</script>

<style lang="scss" scoped>
.boxes-wrapper {
  min-height: 145px;
}
</style>
